import React, { useMemo, useRef, useState } from 'react';

import { useTheme } from '../../styles/ThemeProvider';

import {

  FilterBar, DateRangePicker, KpiCard, ChartCard,

  Waterfall, TrendLine,

} from '../../components/analytics';

import { formatTRY, formatPct, formatCompact } from '../../utils/analytics/format';

import { useSearchParams } from 'react-router-dom';

import { rangeFromUrl } from '../../utils/analytics/dateRange';

import { useAnalyticsQuery } from '../../hooks/useAnalyticsQuery';

import { useFirmalar } from '../../hooks/useFirmalar';



const API = process.env.REACT_APP_API_URL || 'http\://localhost:8001';



const authHeaders = () => {

  const token = localStorage.getItem('access_token');

  return token ? { Authorization: `Bearer ${token}` } : {};

};



// OPEX bucket etiket/sıra FALLBACK'i — asıl kaynak backend response'taki

// opex_bucket_meta (Anayasa M.1: yeni bucket backend'e eklenince frontend

// kendiliğinden gösterir). Bu sabitler yalnız meta gelmezse devreye girer.

const BUCKET_LABELS = {

  PERSONNEL: 'Personel',

  RENT: 'Kira',

  UTILITIES: 'Enerji & Telekom',

  MARKETING: 'Pazarlama & Dijital',

  OPERATIONS: 'Operasyon (Bakım/Sarf/Lojistik)',

  G_AND_A: 'Genel Yönetim',

  OTHER_OPEX: 'Diğer Faaliyet Giderleri',

  D_AND_A: 'Amortisman (D&A)',

  FINANCE: 'Finansman Giderleri',

};



const OPEX_BUCKET_ORDER = ['PERSONNEL', 'RENT', 'UTILITIES', 'MARKETING', 'OPERATIONS', 'G_AND_A', 'OTHER_OPEX'];



export default function KarZararAnalizPage({ embedded = false }) {

  const { theme } = useTheme();

  // Cockpit drill-down bağlamı: URL'de baslangic/bitis/firma varsa onlarla aç.

  const [searchParams] = useSearchParams();

  const [range, setRange] = useState(() => rangeFromUrl(searchParams));

  const [firma, setFirma] = useState(() => searchParams.get('firma') || ''); // '' = konsolide

  const containerRef = useRef(null);

  const { data: firmalar } = useFirmalar();



  const params = useMemo(() => {

    const p = new URLSearchParams();

    if (range.start) p.set('baslangic', range.start);

    if (range.end) p.set('bitis', range.end);

    if (firma) p.set('firma', firma);

    return p.toString();

  }, [range, firma]);



  // EBITDA veri kaynağı:
  // false = yeni EBITDA V2 aktif
  // true  = eski EBITDA (geri dönüş için kodda tutuluyor)
  const USE_LEGACY_EBITDA = false;

  const EBITDA_ENDPOINT = USE_LEGACY_EBITDA
    ? '/api/reports/ebitda'
    : '/api/reports/ebitda-v2';

  const { data, loading, error } = useAnalyticsQuery(

    `ebitda:${USE_LEGACY_EBITDA ? 'legacy' : 'v2'}:${params}`,

    async ({ signal }) => {

      const res = await fetch(`${API}${EBITDA_ENDPOINT}?${params}`, { signal, headers: authHeaders() });

      if (!res.ok) throw new Error(`Hata ${res.status}`);

      return res.json();

    },

    [params],

  );



  const cur = useMemo(() => data?.current || {}, [data]);

  const cmp = useMemo(() => data?.karsilastirma || {}, [data]);

  const trend = useMemo(() => data?.trend || [], [data]);

  const firmaOzet = useMemo(() => data?.firma_ozet || {}, [data]);

  // Bucket etiket+sıra backend'den (tek kaynak); meta yoksa modül fallback'i

  const bucketMeta = useMemo(() => {

    const meta = Array.isArray(data?.opex_bucket_meta) ? data.opex_bucket_meta : null;

    if (!meta || meta.length === 0) {

      return { order: OPEX_BUCKET_ORDER, labels: BUCKET_LABELS };

    }

    return {

      order: meta.map(b => b.kod),

      labels: Object.fromEntries(meta.map(b => [b.kod, b.label])),

    };

  }, [data]);



  // Waterfall: Net Ciro → SMM → Brüt → OPEX → EBITDA → D&A → EBIT → Finansman → Net Kâr

  const waterfallItems = useMemo(() => {

    if (!cur || !cur.net_ciro) return [];

    return [

      { label: 'Net Ciro', value: cur.net_ciro },

      { label: 'SMM', value: -(cur.smm || 0) },

      { label: 'Brüt Kâr', total: true },

      { label: 'OPEX', value: -(cur.opex_toplam || 0) },

      // Fire/zayi + ikram-iptal artık OPEX dışı açık zarar (backend formülüyle aynı)

      { label: 'Fire & İkram/İptal', value: -((cur.fire_zayi || 0) + (cur.ikram_iptal_maliyet || 0)) },

      { label: 'EBITDA', total: true },

      { label: 'Amortisman', value: -(cur.d_and_a || 0) },

      { label: 'Finansman', value: -(cur.finansman || 0) },

      { label: 'Net Kâr', total: true },

    ];

  }, [cur]);



  // Trend chart data

  const trendData = useMemo(() => {

    return trend.map(t => ({

      x: t.ay,

      ebitda: t.ebitda,

      net_kar: t.net_kar,

    }));

  }, [trend]);



  const Wrapper = ({ children }) =>

    embedded ? (

      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.space.lg, fontFamily: theme.font.family }}>

        {children}

      </div>

    ) : (

      <div ref={containerRef} style={{

        padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg,

        background: theme.color.surfaceAlt, minHeight: '100vh', fontFamily: theme.font.family,

      }}>{children}</div>

    );



  return (

    <Wrapper>

      {!embedded && (

        <header>

          <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>

            Kâr/Zarar — EBITDA Raporu

          </h1>

          <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>

            Kurumsal P&L hiyerarşisi · Net Ciro → Brüt Kâr → EBITDA → Net Kâr

          </div>

        </header>

      )}



      <FilterBar sticky={!embedded}>

        <DateRangePicker value={range} onChange={setRange} />

        <FirmaChips firma={firma} setFirma={setFirma} firmalar={firmalar} firmaOzet={firmaOzet} theme={theme} />



      </FilterBar>



      {error && <div style={{ color: theme.color.danger }}>{`Hata: ${error}`}</div>}



      {/* === ÜST KPI BANDI — 5 metrik === */}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.space.base }}>

        <KpiCard title="Net Ciro" value={loading ? undefined : formatTRY(cur.net_ciro)} loading={loading}

                 deltaLabel={`MoM`} delta={cmp.net_ciro_delta_pct} />

        <KpiCard title="Brüt Marj" value={loading ? undefined : formatPct(cur.brut_marj_pct)} loading={loading}

                 deltaLabel={loading ? '' : formatTRY(cur.brut_kar)} />

        <KpiCard title="EBITDA" value={loading ? undefined : formatTRY(cur.ebitda)} loading={loading}

                 deltaLabel={`MoM`} delta={cmp.ebitda_delta_pct} />

        <KpiCard title="EBITDA Marjı" value={loading ? undefined : formatPct(cur.ebitda_marj_pct)} loading={loading}

                 deltaLabel={loading ? '' : `Sektör hedefi: %15+`} />

        <KpiCard title="Net Kâr" value={loading ? undefined : formatTRY(cur.net_kar)} loading={loading}

                 deltaLabel={loading ? '' : `Marj: ${formatPct(cur.net_marj_pct)}`}

                 delta={cmp.net_kar_delta_pct} />

      </div>



      {/* === EBITDA WATERFALL === */}

      <ChartCard title="EBITDA Bridge" subtitle="Net Ciro'dan Net Kâr'a değer akışı"

                 loading={loading} error={error} empty={!loading && waterfallItems.length === 0} height={320}>

        <Waterfall items={waterfallItems} formatter={formatCompact} />

      </ChartCard>



      {/* === KURUMSAL P&L STATEMENT TABLOSU === */}

      <PnlStatement cur={cur} prev={cmp.onceki || {}} loading={loading} theme={theme} bucketMeta={bucketMeta} />



      {/* === RESTORAN KPI'LARI === */}

      <RestoranKpileri cur={cur} loading={loading} theme={theme} />



      {/* === VERİ MUTABAKATI & SMM KAPSAMI (BG-02/BG-03) === */}

      <MutabakatKapsamPanel mutabakat={data?.mutabakat} loading={loading} theme={theme} />



      {/* === 6 AY TREND === */}

      {/* BG-06: trend her zaman TAM AY bazlıdır — kısmi dönem seçilse de son bar

          tüm ayı gösterir; üstteki kartlarla farkı etiketle açıklıyoruz. */}

      <ChartCard title="Son 6 Ay EBITDA Trendi"

                 subtitle="Tam ay bazlı — seçili dönem kısmi ise son ay barı tüm ayı kapsar"

                 loading={loading} error={error}

                 empty={!loading && trendData.length === 0} height={280}>

        <TrendLine

          data={trendData}

          xKey="x"

          series={[

            { key: 'ebitda', label: 'EBITDA' },

            { key: 'net_kar', label: 'Net Kâr' },

          ]}

          yFormatter={formatCompact}

        />

      </ChartCard>



      {/* === OPEX KOMPOZİSYONU === */}

      <OpexKompozisyon cur={cur} loading={loading} theme={theme} bucketMeta={bucketMeta} />

    </Wrapper>

  );

}



// ─── Firma Chip Selector ────────────────────────────────────────

function FirmaChips({ firma, setFirma, firmalar, firmaOzet, theme }) {

  const chips = useMemo(() => {

    const out = [{ kod: '', ad: 'Tamamı' }];

    (firmalar || [])

      .filter(f => f.kod !== 'OFIS')

      .forEach(f => out.push(f));

    return out;

  }, [firmalar]);



  return (

    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>

      {chips.map(f => {

        const ozet = firmaOzet[f.kod];

        const aktif = firma === f.kod;

        return (

          <button

            key={f.kod || 'konsolide'}

            onClick={() => setFirma(f.kod)}

            title={ozet ? `EBITDA: ${formatCompact(ozet.ebitda)} (${formatPct(ozet.ebitda_marj_pct)})` : ''}

            style={{

              padding: '6px 12px',

              borderRadius: 16,

              border: `1px solid ${aktif ? theme.color.primary : theme.color.border}`,

              background: aktif ? theme.color.primary : theme.color.surface,

              color: aktif ? 'white' : theme.color.text,

              fontSize: theme.font.size.xs,

              cursor: 'pointer',

              fontWeight: aktif ? 600 : 400,

              whiteSpace: 'nowrap',

            }}

          >

            {f.ad}

          </button>

        );

      })}

    </div>

  );

}



// ─── Kurumsal P&L Statement Tablosu ─────────────────────────────

function PnlStatement({ cur, prev, loading, theme, bucketMeta }) {

  const opexOrder = bucketMeta?.order || OPEX_BUCKET_ORDER;

  const bucketLabels = bucketMeta?.labels || BUCKET_LABELS;

  const tableBg = theme.color.surface;

  const border = theme.color.border;

  const text = theme.color.text;

  const soft = theme.color.textSoft;



  const calcDelta = (c, p) => {

    if (!p) return null;

    return ((c - p) / Math.abs(p)) * 100;

  };



  const Row = ({ label, value, prevValue, pctOfRevenue, isSubtotal, isTotal, indent = 0, isExpense }) => {

    const delta = calcDelta(value || 0, prevValue || 0);

    // Gider satırında artış KÖTÜ (kırmızı), azalış İYİ (yeşil); gelir/ara-toplamda tersi

    const deltaColor = delta == null ? soft

      : ((isExpense ? delta <= 0 : delta >= 0) ? theme.color.success : theme.color.danger);

    const valColor = isExpense ? theme.color.danger : (isSubtotal || isTotal) ? text : text;

    const fontWeight = isTotal ? 700 : isSubtotal ? 600 : 400;

    const bgColor = isTotal ? `${theme.color.primary}15` : isSubtotal ? `${theme.color.surface}` : 'transparent';

    const borderTop = isSubtotal || isTotal ? `1px solid ${border}` : 'none';



    return (

      <tr style={{ background: bgColor, borderTop }}>

        <td style={{ padding: '10px 16px', paddingLeft: 16 + indent * 20, color: text, fontWeight }}>

          {label}

        </td>

        <td style={{ padding: '10px 16px', textAlign: 'right', color: valColor, fontWeight, fontVariantNumeric: 'tabular-nums' }}>

          {isExpense && value > 0 ? '(' + formatTRY(value) + ')' : formatTRY(value || 0)}

        </td>

        <td style={{ padding: '10px 16px', textAlign: 'right', color: soft, fontSize: theme.font.size.xs, fontVariantNumeric: 'tabular-nums' }}>

          {pctOfRevenue !== undefined ? formatPct(pctOfRevenue) : '—'}

        </td>

        <td style={{ padding: '10px 16px', textAlign: 'right', color: soft, fontSize: theme.font.size.xs, fontVariantNumeric: 'tabular-nums' }}>

          {prevValue !== undefined ? formatTRY(prevValue || 0) : '—'}

        </td>

        <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: theme.font.size.xs, fontVariantNumeric: 'tabular-nums', color: deltaColor }}>

          {delta == null ? '—' : (delta >= 0 ? '▲' : '▼') + ' ' + Math.abs(delta).toFixed(1) + '%'}

        </td>

      </tr>

    );

  };



  if (loading) {

    return <ChartCard title="Gelir Tablosu (P&L Statement)" loading={true} height={400} />;

  }



  return (

    <ChartCard title="Gelir Tablosu (P&L Statement)" subtitle="Kademeli kurumsal görünüm — Tutar / %Ciro / Önceki Dönem / Δ%" autoHeight>

      <div style={{ overflowX: 'auto' }}>

        <table style={{ width: '100%', borderCollapse: 'collapse', background: tableBg, fontSize: theme.font.size.sm, fontFamily: theme.font.family }}>

          <thead>

            <tr style={{ background: theme.color.surfaceAlt, borderBottom: `2px solid ${border}` }}>

              <th style={{ padding: 12, textAlign: 'left', color: soft, fontWeight: 600 }}>Kalem</th>

              <th style={{ padding: 12, textAlign: 'right', color: soft, fontWeight: 600 }}>Tutar</th>

              <th style={{ padding: 12, textAlign: 'right', color: soft, fontWeight: 600 }}>% Ciro</th>

              <th style={{ padding: 12, textAlign: 'right', color: soft, fontWeight: 600 }}>Önceki Dönem</th>

              <th style={{ padding: 12, textAlign: 'right', color: soft, fontWeight: 600 }}>Δ MoM</th>

            </tr>

          </thead>

          <tbody>

            <Row label="Net Ciro" value={cur.net_ciro} prevValue={prev.net_ciro} pctOfRevenue={100} />

            <Row label="(−) Satılan Malın Maliyeti (SMM)" value={cur.smm} prevValue={prev.smm} pctOfRevenue={cur.smm_pct} indent={1} isExpense />

            <Row label="BRÜT KÂR" value={cur.brut_kar} prevValue={prev.brut_kar} pctOfRevenue={cur.brut_marj_pct} isSubtotal />



            <tr><td colSpan={5} style={{ padding: '8px 16px', color: soft, fontSize: theme.font.size.xs, fontStyle: 'italic', background: theme.color.surfaceAlt }}>OPEX (Faaliyet Giderleri)</td></tr>

            {opexOrder.map(b => (

              <Row key={b}

                   label={`(−) ${bucketLabels[b] || b}`}

                   value={cur.opex?.[b] || 0}

                   prevValue={prev.opex?.[b] || 0}

                   pctOfRevenue={cur.net_ciro > 0 ? ((cur.opex?.[b] || 0) / cur.net_ciro * 100) : 0}

                   indent={1} isExpense />

            ))}

            {/* Fire & ikram/iptal: OPEX bucket'larına GÖMÜLMEZ — açık zarar satırları

                (ebitda = brüt − opex − fire − ikram; satır toplamı artık tablo ile tutar) */}

            <Row label="(−) Fire & Zayi" value={cur.fire_zayi} prevValue={prev.fire_zayi} pctOfRevenue={cur.fire_zayi_pct} indent={1} isExpense />

            <Row label="(−) İkram & İptal Maliyeti" value={cur.ikram_iptal_maliyet} prevValue={prev.ikram_iptal_maliyet} pctOfRevenue={cur.ikram_iptal_pct} indent={1} isExpense />



            <Row label="EBITDA" value={cur.ebitda} prevValue={prev.ebitda} pctOfRevenue={cur.ebitda_marj_pct} isSubtotal />

            <Row label="(−) Amortisman (D&A)" value={cur.d_and_a} prevValue={prev.d_and_a} pctOfRevenue={cur.net_ciro > 0 ? (cur.d_and_a / cur.net_ciro * 100) : 0} indent={1} isExpense />

            <Row label="EBIT (Faaliyet Kârı)" value={cur.ebit} prevValue={prev.ebit} pctOfRevenue={cur.ebit_marj_pct} isSubtotal />

            <Row label="(−) Finansman Giderleri" value={cur.finansman} prevValue={prev.finansman} pctOfRevenue={cur.net_ciro > 0 ? (cur.finansman / cur.net_ciro * 100) : 0} indent={1} isExpense />

            <Row label="NET KÂR" value={cur.net_kar} prevValue={prev.net_kar} pctOfRevenue={cur.net_marj_pct} isTotal />

          </tbody>

        </table>

      </div>

    </ChartCard>

  );

}



// ─── Restoran KPI'ları ──────────────────────────────────────────

function RestoranKpileri({ cur, loading, theme }) {

  const kpis = [

    { label: 'Food Cost', value: cur.food_cost_pct, hedef: 30, hint: 'Satılan ürünün hammadde maliyeti / Net ciro · Hedef: %25-30' },

    { label: 'Labor Cost', value: cur.labor_cost_pct, hedef: 30, hint: 'Personel gideri / Net ciro · Hedef: %25-30' },

    { label: 'Prime Cost', value: cur.prime_cost_pct, hedef: 60, hint: 'Food + Labor (en kritik restoran KPI) · Hedef: %55-60' },

    { label: 'Fire/Zayi', value: cur.fire_zayi_pct, hedef: 2, hint: 'Hedef: %2 altında' },

  ];

  return (

    <ChartCard title="Restoran Operasyon KPI'ları" subtitle="Sektör benchmarklarına göre durum" autoHeight>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: theme.space.base, padding: theme.space.base }}>

        {kpis.map(k => {

          const val = k.value || 0;

          const ok = val <= k.hedef;

          return (

            <div key={k.label} title={k.hint} style={{

              background: theme.color.surfaceAlt, borderRadius: theme.radius.md,

              padding: theme.space.base, border: `1px solid ${border(theme, ok)}`,

            }}>

              <div style={{ fontSize: theme.font.size.xs, color: theme.color.textSoft, marginBottom: 4 }}>{k.label}</div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>

                <span style={{ fontSize: 26, fontWeight: 700, color: ok ? theme.color.success : theme.color.danger, fontVariantNumeric: 'tabular-nums' }}>

                  {loading ? '…' : formatPct(val)}

                </span>

                <span style={{ fontSize: theme.font.size.xs, color: theme.color.textSoft }}>

                  / hedef ≤ {k.hedef}%

                </span>

              </div>

            </div>

          );

        })}

      </div>

    </ChartCard>

  );

}



const border = (theme, ok) => ok ? theme.color.border : theme.color.danger;



// ─── Veri Mutabakatı & SMM Kapsamı (BG-02/BG-03) ────────────────

// Rakamları DEĞİŞTİRMEZ — raporun beslendiği iki kaynağın (POS başlık net_ciro

// ↔ satış satırları) tutarlılığını ve SMM'nin satışın ne kadarını kapsadığını

// görünür kılar. İkram/iptal maliyeti SMM'ye dahil DEĞİLDİR (bilgi satırı).

function MutabakatKapsamPanel({ mutabakat, loading, theme }) {

  if (loading || !mutabakat) return null;

  const ca = mutabakat.ciro_akisi || {};

  const sm = mutabakat.satir_mutabakat || {};

  const kapsam = mutabakat.smm_kapsam || {};

  const disi = mutabakat.smm_disi_maliyet || {};

  const tol = mutabakat.tolerans_tl || 1;

  const soft = theme.color.textSoft;

  const text = theme.color.text;



  const uyarilar = [];

  if ((ca.tutarsiz_run_sayisi || 0) > 0) {

    uyarilar.push(`${ca.tutarsiz_run_sayisi} POS raporunda net ciro, "brüt − ödenmeyen − indirim" hesabına uymuyor (toplam sapma ${formatTRY(ca.fark)}).`);

  }

  if ((sm.tutarsiz_run_sayisi || 0) > 0) {

    uyarilar.push(`${sm.tutarsiz_run_sayisi} POS raporunda satış satırları toplamı brüt ciroyla uyuşmuyor (fark ${formatTRY(sm.fark)}).`);

  }

  const kapsamaDusuk = (kapsam.kapsama_pct || 0) < 95 && (sm.satis_satir_ciro || 0) > 0;



  const Satir = ({ label, value, negatif, kalin }) => (

    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: theme.font.size.sm, fontWeight: kalin ? 700 : 400, borderTop: kalin ? `1px solid ${theme.color.border}` : 'none' }}>

      <span style={{ color: kalin ? text : soft }}>{label}</span>

      <span style={{ color: negatif ? theme.color.danger : text, fontVariantNumeric: 'tabular-nums' }}>

        {negatif ? '(' + formatTRY(value || 0) + ')' : formatTRY(value || 0)}

      </span>

    </div>

  );



  return (

    <ChartCard title="Veri Mutabakatı & SMM Kapsamı"

               subtitle="Raporun beslendiği kaynakların tutarlılık kontrolü — rakamları değiştirmez" autoHeight>

      <div style={{ padding: theme.space.base, display: 'flex', flexDirection: 'column', gap: theme.space.base }}>

        {uyarilar.map((u, i) => (

          <div key={i} style={{

            background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: theme.radius.sm,

            padding: '8px 12px', color: '#92400E', fontSize: theme.font.size.sm,

          }}>⚠️ {u}</div>

        ))}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: theme.space.base }}>

          {/* Ciro akışı */}

          <div style={{ background: theme.color.surfaceAlt, borderRadius: theme.radius.md, padding: theme.space.base, border: `1px solid ${theme.color.border}` }}>

            <div style={{ fontSize: theme.font.size.xs, color: soft, marginBottom: 8, fontWeight: 600 }}>

              CİRO AKIŞI (POS ödeme bölümü bazlı)

            </div>

            <Satir label="Brüt POS Ciro" value={ca.brut_ciro} />

            <Satir label="(−) Ödenmeyen" value={ca.odenmeyen} negatif />

            <Satir label="(−) İndirim" value={ca.indirim} negatif />

            <Satir label="Net Ciro (raporda kullanılan)" value={ca.net_ciro} kalin />

            {Math.abs(ca.fark || 0) > tol && (

              <div style={{ fontSize: theme.font.size.xs, color: theme.color.danger, marginTop: 6 }}>

                Mutabakat farkı: {formatTRY(ca.fark)}

              </div>

            )}

            <div style={{ fontSize: theme.font.size.xs, color: soft, marginTop: 6 }}>

              Satış satırları toplamı: {formatTRY(sm.satis_satir_ciro)} · brüt ile fark: {formatTRY(sm.fark)}

            </div>

          </div>

          {/* SMM kapsamı */}

          <div style={{ background: theme.color.surfaceAlt, borderRadius: theme.radius.md, padding: theme.space.base, border: `1px solid ${kapsamaDusuk ? '#F59E0B' : theme.color.border}` }}>

            <div style={{ fontSize: theme.font.size.xs, color: soft, marginBottom: 8, fontWeight: 600 }}>

              SMM KAPSAMI (maliyeti bilinen satış)

            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>

              <span style={{ fontSize: 26, fontWeight: 700, color: kapsamaDusuk ? '#B45309' : theme.color.success, fontVariantNumeric: 'tabular-nums' }}>

                {formatPct(kapsam.kapsama_pct)}

              </span>

              <span style={{ fontSize: theme.font.size.xs, color: soft }}>satış cirosunun maliyeti SMM'de</span>

            </div>

            <div style={{ marginTop: 8 }}>

              <Satir label="Maliyeti bilinen satış" value={kapsam.kapsanan_ciro} />

              <Satir label="Eşleşmeyen satış (maliyet bilinmiyor)" value={kapsam.eslesmeyen_ciro} />

              <Satir label="Eşleşmiş ama maliyetsiz satış" value={kapsam.maliyetsiz_ciro} />

            </div>

            {((disi.ikram_maliyet || 0) > 0 || (disi.iptal_maliyet || 0) > 0) && (

              <div style={{ fontSize: theme.font.size.xs, color: soft, marginTop: 8, borderTop: `1px dashed ${theme.color.border}`, paddingTop: 6 }}>

                SMM dışı tüketim (P&L'de "İkram & İptal Maliyeti" zarar satırı olarak düşülür) —

                İkram: {formatTRY(disi.ikram_maliyet)}

                {(disi.iptal_maliyet || 0) > 0 ? ` · İptal: ${formatTRY(disi.iptal_maliyet)}` : ''}

              </div>

            )}

          </div>

        </div>

      </div>

    </ChartCard>

  );

}



// ─── OPEX Kompozisyonu (yatay stack bar) ────────────────────────

function OpexKompozisyon({ cur, loading, theme, bucketMeta }) {

  const opex = cur.opex || {};

  const total = cur.opex_toplam || 0;

  if (loading || total === 0) {

    return <ChartCard title="OPEX Kompozisyonu" loading={loading} empty={!loading} height={120} />;

  }

  const opexOrder = bucketMeta?.order || OPEX_BUCKET_ORDER;

  const bucketLabels = bucketMeta?.labels || BUCKET_LABELS;

  const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#7f7f7f'];

  const items = opexOrder

    .map((b, i) => ({ bucket: b, label: bucketLabels[b] || b, value: opex[b] || 0, color: colors[i % colors.length] }))

    .filter(x => x.value > 0);



  return (

    <ChartCard title="OPEX Kompozisyonu" subtitle={`Toplam: ${formatTRY(total)} (Net cironun ${formatPct(cur.opex_pct)})`} autoHeight>

      <div style={{ padding: theme.space.base }}>

        <div style={{ display: 'flex', height: 32, borderRadius: theme.radius.sm, overflow: 'hidden', border: `1px solid ${theme.color.border}` }}>

          {items.map(it => {

            const pct = (it.value / total) * 100;

            return (

              <div key={it.bucket}

                   title={`${it.label}: ${formatTRY(it.value)} (${pct.toFixed(1)}%)`}

                   style={{ width: `${pct}%`, background: it.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 11, fontWeight: 600 }}>

                {pct >= 8 ? `${pct.toFixed(0)}%` : ''}

              </div>

            );

          })}

        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>

          {items.map(it => (

            <div key={it.bucket} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: theme.font.size.xs, color: theme.color.text }}>

              <span style={{ width: 12, height: 12, background: it.color, borderRadius: 2, display: 'inline-block' }} />

              <span>{it.label}</span>

              <span style={{ color: theme.color.textSoft }}>· {formatTRY(it.value)}</span>

            </div>

          ))}

        </div>

      </div>

    </ChartCard>

  );

}
